/* eslint-disable tss-unused-classes/unused-classes */
import { makeStyles } from 'tss-react/mui';
import { CustomFilter } from 'app/ComponentLibrary/Filter';
import { defaultFilterName, SearchType } from 'app/models/GlobalSearch/GlobalSearch';
import {
  getCitiesAndZipsByStates,
  getPlacementStatusTreeOptions,
  getSearchFilterOptions,
} from 'app/services/SharedServices/SharedServices';
import { userDevicePreferenceActions } from 'oidc/UserDevicePreference/userPreference.redux';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';
import { useDispatch, useSelector } from 'react-redux';
import { SessionKey, removeSessionValue, StorageType } from 'utils/customHooks/sessionStorage/sessionHelpers';
import { getMultiSelectOptions } from '../helper';
import { getPlacementTagsOpts, PlacementsFilter } from './FilterUtils';
import { RootState } from 'types';
import { Collapse, Grid } from '@mui/material';
import { SearchPageIcons } from 'app/ComponentLibrary/Filter/SearchPageIcons';
import { gridStateActions } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.redux';
import { selectFilters, selectGridFiltersExpanded } from '../../Common/Grid/GridStateManagement/GridState.selectors';
import tagsData from 'app/assets/jsons/PlacementTagsV2.json';
import placementSubmission from 'app/assets/jsons/PlacementSubmissionFilter.json';
import { useDecision } from '@optimizely/react-sdk';
import { ff_placement_ui_arbitration_agreement_grids } from 'app/constants/FeatureFlags/Global/keys';
import { ff_placement_region_fields } from 'app/constants/FeatureFlags/Placement/Keys';
import { resetFilters } from './helper';
import { cloneDeep } from 'lodash';
import { IStateCityZip } from 'app/models/Candidate/IStateCityZip';
import { getAllCitiesAndZips } from '../Candidate/FilterUtils';

const useStyles = makeStyles()({
  accountManagerField: {
    minWidth: 200,
  },
  placementRecruiterField: {
    minWidth: 155,
  },
  archiveOptsField: {
    minWidth: 126,
  },
  amDivisionField: {
    minWidth: 126,
  },
  affiliateField: {
    minWidth: 130,
  },
  fillterCollapse: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '3px',
  },
  collapse: {
    paddingTop: '4px',
  },
  recruitmentLeaders: {
    '& .MuiAutocomplete-listbox': {
      maxHeight: '150px',
    },
    marginTop: 4,
    zIndex: 5000,
  },
});

export const PlacementSearchFilter = ({ defaultFilterValues }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [arbitrationAgreementFlag] = useDecision(ff_placement_ui_arbitration_agreement_grids);
  const [regionFlag] = useDecision(ff_placement_region_fields);
  const { setValue } = useFormContext(); //temp fix for extension status filter chips
  const gridStateFilters = useSelector(selectFilters);
  const filterPreferences = useSelector((state: RootState) => {
    return state.userPreferenceData.userGridPreferences.find(item => item.id === 'GlobalSearchGrid-placement');
  });
  const allCitiesandZipsData = useRef<IStateCityZip[]>();
  const gridSpecs = useRef(
    new PlacementsFilter(
      {
        brandOpts: [],
        amnDivisionOpts: [],
        orderTypeOpts: [],
        stateOpts: [],
        cityOptions: [],
        zipOptions: [],
        mspClientOpts: [],
        disciplineOpts: [],
        specialtyOpts: [],
        statusOpts: [],
        submissionOpts: [],
        extensionOpts: [],
        screenOpts: [],
        tagsOptions: [],
        staffingVendorOpts: [],
        techVendorOpts: [],
        businessRelationshipOpts: [],
      },
      t,
      classes,
      arbitrationAgreementFlag.enabled,
      regionFlag.enabled,
      gridStateFilters,
    ),
  );
  const [fields, setFields] = useState<any>(gridSpecs.current.getPlacementSearchFilterSpecs());

  const updateGridState = useCallback(
    async (selectedStates: string[], selectedCities: string[], gs: any) => {
      if (!gs || !allCitiesandZipsData.current) return;
      let filteredZips: IStateCityZip[];
      let filteredCities: IStateCityZip[];
      if (!selectedStates.length) filteredCities = allCitiesandZipsData.current;
      else {
        filteredCities = (await getCitiesAndZipsByStates({
          states: selectedStates,
          cities: [],
        })) as IStateCityZip[];
      }

      if (!selectedCities.length) filteredZips = filteredCities;
      else {
        filteredZips = (await getCitiesAndZipsByStates({
          states: selectedStates,
          cities: selectedCities,
        })) as IStateCityZip[];
      }

      gridSpecs.current.filterSpecs.cityOptions = getAllCitiesAndZips(filteredCities).cities;
      gridSpecs.current.filterSpecs.zipOptions = getAllCitiesAndZips(filteredZips).zips;
      gridSpecs.current.gridStateFilters = gs;
      setFields(gridSpecs.current.getPlacementSearchFilterSpecs());
    },
    [],
  );

  const fetchPlacementFilterSpecs = async () => {
    getSearchFilterOptions('brands', true).then(res => {
      gridSpecs.current.filterSpecs.brandOpts = getMultiSelectOptions(res || [], { withoutAllOption: true });
      const fieldsSchema = gridSpecs.current.getPlacementSearchFilterSpecs();
      setFields(fieldsSchema);
    });
    getSearchFilterOptions('divisions', true).then(res => {
      gridSpecs.current.filterSpecs.amnDivisionOpts = getMultiSelectOptions(res || [], { withoutAllOption: true });
      const fieldsSchema = gridSpecs.current.getPlacementSearchFilterSpecs();
      setFields(fieldsSchema);
    });
    getSearchFilterOptions('ordertypes', true).then(res => {
      gridSpecs.current.filterSpecs.orderTypeOpts = getMultiSelectOptions(res || [], { withoutAllOption: true });
      const fieldsSchema = gridSpecs.current.getPlacementSearchFilterSpecs();
      setFields(fieldsSchema);
    });
    getSearchFilterOptions('screen', true).then(res => {
      gridSpecs.current.filterSpecs.screenOpts = getMultiSelectOptions(res || [], { withoutAllOption: true });
      const fieldsSchema = gridSpecs.current.getPlacementSearchFilterSpecs();
      setFields(fieldsSchema);
    });
    getSearchFilterOptions('states', true).then(async res => {
      gridSpecs.current.filterSpecs.stateOpts = getMultiSelectOptions(res || [], { withoutAllOption: true });
      const statesLookups = res;
      allCitiesandZipsData.current = (await getCitiesAndZipsByStates({
        states: [statesLookups?.[0]?.name],
        cities: undefined,
      })) as IStateCityZip[];
      const allCitiesAndZips = getAllCitiesAndZips(allCitiesandZipsData.current);
      gridSpecs.current.filterSpecs.cityOptions = allCitiesAndZips?.cities;
      gridSpecs.current.filterSpecs.zipOptions = allCitiesAndZips?.zips;
      const fieldsSchema = gridSpecs.current.getPlacementSearchFilterSpecs();
      setFields(fieldsSchema);
    });
    getSearchFilterOptions('mspclients', true).then(res => {
      gridSpecs.current.filterSpecs.mspClientOpts = getMultiSelectOptions(res || [], { withoutAllOption: true });
      const fieldsSchema = gridSpecs.current.getPlacementSearchFilterSpecs();
      setFields(fieldsSchema);
    });
    getSearchFilterOptions('disciplines', true).then(res => {
      gridSpecs.current.filterSpecs.disciplineOpts = getMultiSelectOptions(res || [], { withoutAllOption: true });
      const fieldsSchema = gridSpecs.current.getPlacementSearchFilterSpecs();
      setFields(fieldsSchema);
    });
    getSearchFilterOptions('businessrelationships', true).then(res => {
      gridSpecs.current.filterSpecs.businessRelationshipOpts = getMultiSelectOptions(res || [], { withoutAllOption: true });
      const fieldsSchema = gridSpecs.current.getPlacementSearchFilterSpecs();
      setFields(fieldsSchema);
    });
    getSearchFilterOptions('specialties', true).then(res => {
      gridSpecs.current.filterSpecs.specialtyOpts = getMultiSelectOptions(res || [], { withoutAllOption: true });
      const fieldsSchema = gridSpecs.current.getPlacementSearchFilterSpecs();
      setFields(fieldsSchema);
    });
    getSearchFilterOptions('extensionStatuses', true).then(res => {
      gridSpecs.current.filterSpecs.extensionOpts = getMultiSelectOptions(res || [], { withoutAllOption: true });
      const fieldsSchema = gridSpecs.current.getPlacementSearchFilterSpecs();
      setFields(fieldsSchema);
    });
    getSearchFilterOptions('staffingVendor', true).then(res => {
      gridSpecs.current.filterSpecs.staffingVendorOpts = getMultiSelectOptions(res || [], { withoutAllOption: true });
      const fieldsSchema = gridSpecs.current.getPlacementSearchFilterSpecs();
      setFields(fieldsSchema);
    });
    getSearchFilterOptions('technologyVendor', true).then(res => {
      gridSpecs.current.filterSpecs.techVendorOpts = getMultiSelectOptions(res || [], { withoutAllOption: true });
      const fieldsSchema = gridSpecs.current.getPlacementSearchFilterSpecs();
      setFields(fieldsSchema);
    });
    getSearchFilterOptions('orderplacementregions', true).then(res => {
      gridSpecs.current.filterSpecs.regionOpts = getMultiSelectOptions(res || [], { withoutAllOption: true });
      const fieldsSchema = gridSpecs.current.getPlacementSearchFilterSpecs();
      setFields(fieldsSchema);
    });
    getPlacementStatusTreeOptions().then(res => {
      gridSpecs.current.filterSpecs.statusOpts = res || [];
      const fieldsSchema = gridSpecs.current.getPlacementSearchFilterSpecs();
      setFields(fieldsSchema);
    });
    getSearchFilterOptions('arbitrationstatuses', true).then(res => {
      gridSpecs.current.filterSpecs.arbitrationStatusOpts = getMultiSelectOptions(res || [], {
        withoutAllOption: true,
      });
      const fieldsSchema = gridSpecs.current.getPlacementSearchFilterSpecs();
      setFields(fieldsSchema);
    });
    gridSpecs.current.filterSpecs.tagsOptions = getPlacementTagsOpts(tagsData || []);
    // submission filter options
    gridSpecs.current.filterSpecs.submissionOpts = getMultiSelectOptions(placementSubmission || [], {
      withoutAllOption: true,
    });
    setFields(gridSpecs.current.getPlacementSearchFilterSpecs());
  };

  const resetFilter = () => {
    const foundDefaultIndex = (filterPreferences.value?.filterAttributes?.filterSet || [])?.find(
      item => item.name === defaultFilterName || item.isDefault,
    );
    const filterPrefClone = cloneDeep(filterPreferences);

    if (!foundDefaultIndex) {
      filterPrefClone.value.selectedFilter = null;
    } else {
      // Default filter found, select it
      filterPrefClone.value.selectedFilter = foundDefaultIndex;
    }
    removeSessionValue(SessionKey['GlobalSearchGrid-placement'], StorageType.sessionStorage);
    dispatch(userDevicePreferenceActions.saveUserGridPreferenceAction(filterPrefClone));
    resetFilters(setValue);
  };

  const filtersExpanded = useSelector(selectGridFiltersExpanded);
  const handleChange = () => {
    dispatch(gridStateActions.setFiltersExpanded(!filtersExpanded));
  };

  useEffect(() => {
    trackPromise(fetchPlacementFilterSpecs(), 'global-search-call');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    gridSpecs.current.gridStateFilters = gridStateFilters;
    setFields(gridSpecs.current.getPlacementSearchFilterSpecs());
  }, [gridStateFilters]);

  useEffect(() => {
    if (!gridStateFilters || !allCitiesandZipsData.current) return;
    const selectedStates = !(gridStateFilters?.filters?.states as any[])?.length
      ? []
      : (gridStateFilters?.filters?.states as any[]).reduce(
          (result, item) => (!item?.isCompactOpt ? [...result, item] : result),
          [],
        );

    const selectedCities = (
      !(gridStateFilters?.filters?.city as any[])?.length ? [] : gridStateFilters?.filters?.city
    ) as any[];
    void updateGridState(
      selectedStates.map(x => x.name),
      selectedCities.map(x => x.name),
      gridStateFilters,
    );
  }, [gridStateFilters, updateGridState]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={11}>
        <Collapse timeout={200} in={filtersExpanded} collapsedSize={45} className={classes.collapse}>
          <CustomFilter
            fields={fields}
            justifyContent={'flex-start'}
            version2
            resetBtn
            analytics={{
              caller: t('search.globalSearch.globalSearchFilter'),
              onApply: true,
              properties: {
                category: SearchType.placement,
              },
            }}
          />
        </Collapse>
      </Grid>
      <Grid item xs={1} className={classes.fillterCollapse}>
        <SearchPageIcons
          resetlbutton
          funnelButton
          onResetForm={() => resetFilter()}
          onAdvancedFilterClick={() => {
            handleChange();
          }}
          checked={filtersExpanded}
        />
      </Grid>
    </Grid>
  );
};
